import React, { useState, useContext, useEffect } from "react"
import "./homepages.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBook, faCompass, faPlus, faTrash, faSpinner } from "@fortawesome/pro-regular-svg-icons"
import { useParams } from "@reach/router"
import { Link } from "gatsby"
import useModuleDB from "./shared/useModuleDB.js"
import { FirebaseContext } from "../Firebase"
import LoadingSpinner from "../../components/loading/LoadingSpinner.js"
import { useIntl } from "react-intl"
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons"
import { Modal } from "react-bootstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import DOMPurify from "dompurify"
import { hasHTMLContent } from "./shared/utils.js"
import { RadioButton } from "primereact/radiobutton"
import { ImageWithFallback } from "./views/display.js"
import { FormattedMessage } from "react-intl"
import "primereact/resources/themes/lara-light-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"

// can be rendered from module page or preview
export const ModuleHomeDisplay = ({ moduleData, moduleId, isPreview }) => {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0)
  const { locale } = useIntl()

  const { module_json } = moduleData || {}

  const {
    skills = [],
    skillsEs = [],
    quote,
    quoteEs,
    title,
    titleEs,
    category,
    categoryEs,
    image,
    quoteAuthor,
    fullDescription,
    fullDescriptionEs,
    shortDescription,
    shortDescriptionEs,
    bannerColor,
    sections = [],
  } = module_json || {}

  const localizedTitle = locale === "es-ES" ? titleEs : title
  const localizedShortDescription = locale === "es-ES" ? shortDescriptionEs : shortDescription
  const localizedFullDescription = locale === "es-ES" ? fullDescriptionEs : fullDescription
  const localizedQuote = locale === "es-ES" ? quoteEs : quote
  const localizedSkills = locale === "es-ES" ? skillsEs : skills
  const localizedCategory = locale === "es-ES" ? categoryEs : category
  const localizedSelectedSectionDescription =
    locale === "es-ES" ? sections[selectedSectionIndex]?.sectionDescriptionEs : sections[selectedSectionIndex]?.sectionDescription
  const localizedSelectedSectionDirections =
    locale === "es-ES" ? sections[selectedSectionIndex]?.sectionDirectionsEs : sections[selectedSectionIndex]?.sectionDirections

  const renderGradientClassName = () => {
    switch (bannerColor) {
      case "blue":
        return "blue-gradient"
      case "red":
        return "red-gradient"
      case "orange":
        return "orange-gradient"
      case "yellow":
        return "yellow-gradient"
      case "green":
        return "green-gradient"
      case "purple":
        return "purple-gradient"
      default:
        return "blue-gradient"
    }
  }

  return (
    <div className="curriculum-home module">
      <div className={`home-banner ${renderGradientClassName()}`}>
        <div className="inner">
          <div className="home-container">
            <div className="home-header">
              <label className="home-label">{localizedCategory}</label>
              <h2 className="home-title">{localizedTitle}</h2>
              <p className="home-description">{localizedShortDescription}</p>
              <ImageWithFallback className="home-image" src={image} />
            </div>
            <div className="home-cta">
              <div>
                <h6>
                  <FormattedMessage id="curriculum-you-can-do-it" defaultMessage="You can do it!" />
                </h6>
                <p>
                  <FormattedMessage id="curriculum-one-step-closer" defaultMessage="You're one step closer to being career ready." />
                </p>
              </div>

              <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${moduleId}/interactive`} style={{ pointerEvents: moduleId ? "auto" : "none" }}>
                <button className="button component-button component-button-default" style={{ padding: "12px 24px" }}>
                  <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="home-container inner">
        <div className="home-content">
          <div className="home-why-complete">
            <h3>
              <FormattedMessage id="curriculum-why-complete-this-module" defaultMessage="Why complete this module" />
            </h3>
            {localizedQuote && (
              <>
                <blockquote>"{localizedQuote}"</blockquote>
                <label>-{quoteAuthor}</label>
              </>
            )}

            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedFullDescription) }} />
          </div>
          {localizedSkills.length > 0 && (
            <div className="home-skills">
              <h6>
                <FormattedMessage id="curriculum-skills-you-will-develop" defaultMessage="Skills you will develop:" />
              </h6>
              <div className="home-skills-list">
                {localizedSkills.map((skill, index) => (
                  <div key={index} className="home-skill">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="home-tasks">
          <div className="home-tasks-header">
            <h3>
              <FormattedMessage id="curriculum-tasks-in-module" defaultMessage="Tasks in this module" />
            </h3>
          </div>
          <div className="home-tasks-container">
            <aside className="home-tasks-aside">
              {sections.map(({ sectionTitle, sectionTitleEs }, index) => (
                <div
                  key={index}
                  className={`home-task ${selectedSectionIndex === index ? "selected" : ""}`}
                  onClick={() => setSelectedSectionIndex(index)}
                >
                  <div className="home-task-number">{index + 1}</div>

                  <div>{locale === "es-ES" ? sectionTitleEs : sectionTitle}</div>
                </div>
              ))}
            </aside>
            <div className="home-task-content">
              <div className="home-task-content-inner">
                <h5>
                  <FormattedMessage id="curriculum-overview" defaultMessage="Overview" />
                </h5>
                <div className="home-task-overview">
                  <div className="home-task-overview-icon">
                    <FontAwesomeIcon icon={faBook} color="#63C0D5" size="2x" />
                  </div>
                  <div>
                    <label>
                      <FormattedMessage id="curriculum-what-you-will-learn" defaultMessage="What you will learn" />
                    </label>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedSelectedSectionDescription) }} />
                  </div>
                </div>

                {hasHTMLContent(localizedSelectedSectionDirections) && (
                  <div className="home-task-overview" style={{ marginTop: "1rem" }}>
                    <div className="home-task-overview-icon">
                      <FontAwesomeIcon icon={faCompass} color="#63C0D5" size="2x" />
                    </div>
                    <div>
                      <label>
                        <FormattedMessage id="curriculum-directions" defaultMessage="Directions" />
                      </label>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedSelectedSectionDirections) }} />
                    </div>
                  </div>
                )}
              </div>
              <div className="home-task-footer">
                <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${moduleId}/interactive${isPreview ? "?preview=1" : ""}`} style={{ pointerEvents: moduleId ? "auto" : "none" }}>
                  <button className="button component-button component-button-default component-button-small">
                    <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Load from page if
export const ModuleHome = ({ isPreview }) => {
  const { moduleId } = useParams()

  const { firebase } = useContext(FirebaseContext)
  const { baseModuleTemplate, isModuleLoading } = useModuleDB(moduleId, firebase)

  if (isModuleLoading || !firebase || !baseModuleTemplate) {
    return <LoadingSpinner />
  }

  return <ModuleHomeDisplay moduleData={baseModuleTemplate} moduleId={moduleId} isPreview={isPreview} />
}

export const EducatorModuleHome = () => {
  const { moduleId } = useParams()
  const { firebase } = useContext(FirebaseContext)
  const { baseModuleTemplate, isModuleLoading, isModulePrivateAndAuthoredByUser } = useModuleDB(moduleId, firebase)
  const [showAssignModal, setShowAssignModal] = useState(false)

  const handleAssignClick = () => {
    setShowAssignModal(true)
  }

  const handleCloseModal = () => {
    setShowAssignModal(false)
  }

  if (isModuleLoading || !firebase || !baseModuleTemplate) {
    return <LoadingSpinner />
  }

  if (!isModulePrivateAndAuthoredByUser && baseModuleTemplate.access_type === "private") {
    return <div>You are not authorized to view this module</div>
  }

  const { module_json: { title, category, sections = [] } = {} } = baseModuleTemplate || {}

  const SectionTimeline = () => (
    <div className="section-timeline">
      <h5>{title}</h5>
      <label style={{ marginBottom: "2rem" }}>{category}</label>
      {sections.map(({ sectionTitle }, index) => (
        <div key={index} className="section-timeline-item">
          <div className="section-timeline-item-number">{index + 1}</div>
          <div className="section-timeline-item-title">{sectionTitle}</div>
        </div>
      ))}
    </div>
  )

  return (
    <>
      <div className="module-creation-page">
        <div className="module-creation-page-navigation">
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <Link to={"/educator/assignments/modules"}>
                <span>
                  <FontAwesomeIcon icon={faArrowLeft} size="md" /> Assignments
                </span>{" "}
              </Link>
              | Assignment Overview
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              {baseModuleTemplate.access_type === "private" && (
                <>
                  {" "}
                  <button className="button component-button component-button-small component-button-ghost">Delete</button>
                  <Link to={`/educator/assignments/modules/edit/${moduleId}`}>
                    <button className="button component-button component-button-small component-button-ghost">Edit</button>
                  </Link>
                </>
              )}
              {/* // new tab link */}
              <a href={`/modules/${moduleId}/interactive?preview=1`} target="_blank" rel="noopener noreferrer">
                <button className="button component-button component-button-small component-button-ghost">Preview Module</button>
              </a>
              <button className="button component-button component-button-small component-button-default" onClick={handleAssignClick}>
                Assign
              </button>
            </div>
          </div>
        </div>

        <div className={`module-creation-page-content`}>
          {/* <div className="module-creation-page-sidebar outline educator-section-timeline">
            <SectionTimeline />
          </div> */}

          <div className="module-creation-page-main">
            <ModuleHome isPreview={true} />
          </div>
        </div>
      </div>

      <AssignModuleModal show={showAssignModal} onClose={handleCloseModal} moduleId={moduleId} title={title} />
    </>
  )
}

export const AssignModuleModal = ({ show, onClose, moduleId, title }) => {
  const { firebase, profile } = useContext(FirebaseContext)

  const [dueDate, setDueDate] = useState(null)
  const [assigneeType, setAssigneeType] = useState("INDIVIDUAL")
  const [allUsersList, setAllUsersList] = useState([])
  const [allGroupsList, setAllGroupsList] = useState([])
  const [searchUsers, setSearchUsers] = useState("")
  const [searchGroups, setSearchGroups] = useState("")
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (firebase) {
      const loadAllUsersAndGroups = async () => {
        try {
          const userList = await firebase.getAllUserListing({ role: "student" })
          setAllUsersList(userList?.data?.users || [])
        } catch (error) {
          console.error("Error fetching users:", error)
        }

        try {
          const groupList = await firebase.groupsListCreatedByUser("student")
          setAllGroupsList(groupList?.data || [])
        } catch (error) {
          console.error("Error fetching groups:", error)
        }
      }

      loadAllUsersAndGroups()
    }
  }, [firebase])

  const handleConfirmAssign = async () => {
    if (!dueDate) {
      alert("Due date is required, please select a date")
      return
    }

    if (isSubmitting) {
      // Prevent multiple submissions if already submitting
      return
    }

    try {
      setIsSubmitting(true)
      const groupPromises = selectedGroups.map((group) => {
        const payload = {
          assignedGroupId: group.id,
          assignedUserId: null,
          moduleId,
          dueDate,
          assigneeType: "GROUP",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createModuleAssignment(payload)
      })

      const individualPromises = selectedUsers.map((user) => {
        const payload = {
          assignedGroupId: null,
          assignedUserId: user.id,
          moduleId,
          dueDate,
          assigneeType: "INDIVIDUAL",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createModuleAssignment(payload)
      })

      await Promise.all([...groupPromises, ...individualPromises])
      onClose()
    } catch (error) {
      console.error("Error assigning module:", error)
      alert("Error assigning module, please contact Wingspans support")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddUser = (user) => {
    if (!selectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
    }
  }

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter((user) => user.id !== userId))
  }

  const handleAddGroup = (group) => {
    if (!selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)) {
      setSelectedGroups([...selectedGroups, group])
    }
  }

  const handleRemoveGroup = (groupId) => {
    setSelectedGroups(selectedGroups.filter((group) => group.id !== groupId))
  }

  const filteredUsers = allUsersList.filter((user) => {
    const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.toLowerCase()
    const email = user?.email?.toLowerCase() ?? ""
    return fullName.includes(searchUsers.toLowerCase()) || email.includes(searchUsers.toLowerCase())
  })

  const filteredGroups = allGroupsList.filter((group) => {
    const groupName = group?.name?.toLowerCase() ?? ""
    return groupName.includes(searchGroups.toLowerCase())
  })

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <h4 style={{ marginBottom: "0" }}>Assign Module</h4>
      </Modal.Header>
      <Modal.Body>
        <label>Assignment Name</label>
        <h6 style={{ marginBottom: "1rem" }}>{title}</h6>

        <label>Due Date</label>
        <DatePicker
          selected={dueDate}
          onChange={(date) => setDueDate(date)}
          dateFormat="MMMM d, yyyy"
          placeholderText="Select a due date"
          className="form-control"
        />

        <label style={{ marginTop: "1rem" }}>Assign to</label>
        <div style={{ display: "flex", gap: "2rem", margin: "1rem 0", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="assigneeTypeIndividual"
              name="assigneeType"
              value="INDIVIDUAL"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "INDIVIDUAL"}
            />
            <label htmlFor="assigneeTypeIndividual" style={{ marginBottom: 0 }}>Individual</label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="assigneeTypeGroup"
              name="assigneeType"
              value="GROUP"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "GROUP"}
            />
            <label htmlFor="assigneeTypeGroup" style={{ marginBottom: 0 }}>Group</label>
          </div>
        </div>

        {assigneeType === "INDIVIDUAL" && (
          <div className="user-dropdown-search">
            <label>Search Users</label>
            <input
              className="form-control"
              type="text"
              placeholder="Search user by name or email"
              value={searchUsers}
              onChange={(e) => setSearchUsers(e.target.value)}
              style={{ maxWidth: "375px" }}
            />
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <label style={{ flex: "1" }}>All Users</label>
              <label style={{ flex: "1" }}>Added Users</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul>
                  {filteredUsers.map((user) => (
                    <li key={user.id} onClick={() => handleAddUser(user)}>
                      <div>
                        {user.first_name} {user.last_name}
                      </div>
                      <a>
                        <FontAwesomeIcon icon={faPlus} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul>
                  {selectedUsers.map((user) => (
                    <li key={user.id} onClick={() => handleRemoveUser(user.id)}>
                      <div>
                        {user.first_name} {user.last_name}
                      </div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {assigneeType === "GROUP" && (
          <div className="user-dropdown-search">
            <label>Search Groups</label>
            <input
              className="form-control"
              type="text"
              placeholder="Search group by name"
              value={searchGroups}
              onChange={(e) => setSearchGroups(e.target.value)}
              style={{ maxWidth: "375px" }}
            />
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <label style={{ flex: "1" }}>All Groups</label>
              <label style={{ flex: "1" }}>Added Groups</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul>
                  {filteredGroups.map((group) => (
                    <li key={group.id} onClick={() => handleAddGroup(group)}>
                      <div>{group.name}</div>
                      <a>
                        <FontAwesomeIcon icon={faPlus} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul>
                  {selectedGroups.map((group) => (
                    <li key={group.id} onClick={() => handleRemoveGroup(group.id)}>
                      <div>{group.name}</div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="button component-button component-button-small component-button-default" onClick={handleConfirmAssign}>
          {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : `Assign`}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
